/**
 * FikoIsEmri Dialog — QR Kodlu İş Emri & Etiket
 * Parça bilgisi → QR (parça ad/no/ölçü/malzeme) + operasyon listesi → yazdırılabilir iş emri/etiket.
 * Atölye (HMI) köprüsü: operatör QR okutunca parça künyesine ulaşır.
 */
import { Component, Input, ChangeDetectionStrategy, ChangeDetectorRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import * as THREE from 'three';
import QRCode from 'qrcode/lib/browser';
import { baglamOku } from '../fiko-uretim-baglam/UretimBaglam';
import { KalicilikService } from '../fiko-kalicilik/kalicilik.service';

interface IsOperasyon { ad: string; sure: number; }

@Component({
  selector: 'fikoyt-is-emri',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule],
  template: `
  <div class="ie">
    <div class="ie-grid">
      <div class="ie-in"><label>Parça Adı</label><input [(ngModel)]="parcaAd" (ngModelChange)="qrYenile()"></div>
      <div class="ie-in"><label>Parça No</label><input [(ngModel)]="parcaNo" (ngModelChange)="qrYenile()"></div>
      <div class="ie-in"><label>Adet</label><input type="number" [(ngModel)]="adet" min="1"></div>
      <div class="ie-in"><label>Malzeme</label><input [(ngModel)]="malzeme" (ngModelChange)="qrYenile()"></div>
      <div class="ie-in"><label>Ölçü (mm)</label><input [(ngModel)]="olcu" (ngModelChange)="qrYenile()"></div>
      <div class="ie-in"><label>Termin</label><input [(ngModel)]="termin"></div>
    </div>
    <button class="ie-mini" (click)="modeldenAl()">⊕ Modelden Ölçü/Malzeme Al</button>

    <div class="ie-op-baslik">Operasyonlar <button class="ie-mini" (click)="opEkle()">＋</button></div>
    @for (o of operasyonlar; track $index) {
      <div class="ie-op">
        <input [(ngModel)]="o.ad" class="ad">
        <input type="number" [(ngModel)]="o.sure" min="0"><span class="dk">dk</span>
        <button class="ie-sil" (click)="opSil($index)">✕</button>
      </div>
    }

    <div class="ie-onizle">
      @if (qrUrl) { <img [src]="qrUrl" class="ie-qr" alt="QR"> }
      <div class="ie-bilgi">
        <div class="ie-bb">{{ parcaAd || 'Parça' }}</div>
        <div class="ie-bs">No: {{ parcaNo || '—' }} · {{ adet }} ad</div>
        <div class="ie-bs">{{ malzeme || '—' }} · {{ olcu || '—' }}</div>
        <div class="ie-bs">Toplam: {{ toplamSure() }} dk</div>
      </div>
    </div>

    <button class="ie-btn kaydet" (click)="kaydet()">💾 İş Emrini Kaydet</button>
    @if (kayitDurum) { <div class="ie-kayit-durum">{{ kayitDurum }}</div> }
    <button class="ie-btn" (click)="yazdir()">🖨️ İş Emri Yazdır</button>
    <button class="ie-btn etiket" (click)="etiketYazdir()">🏷️ Etiket Yazdır (QR)</button>
    @if (hata) { <div class="ie-hata">{{ hata }}</div> }
  </div>
  `,
  styles: [`
    .ie { padding:10px; min-width:300px; max-width:340px; font-size:11px; color:#e6f6ff;
      background:linear-gradient(180deg,rgba(0,40,60,.96),rgba(0,18,36,.98)); border-radius:6px; }
    .ie-grid { display:grid; grid-template-columns:1fr 1fr; gap:6px; margin-bottom:6px; }
    .ie-in { display:flex; flex-direction:column; gap:2px; }
    .ie-in label { font-size:9px; color:#5fd9f5; }
    .ie-in input, .ie-op input { background:rgba(0,60,80,.8); border:1px solid rgba(0,200,220,.35); color:#fff; padding:4px; border-radius:4px; font-size:10px; outline:none; box-sizing:border-box; width:100%; }
    .ie-mini { background:rgba(0,90,110,.6); border:1px solid rgba(0,200,220,.4); color:#bdf; border-radius:4px; padding:3px 8px; font-size:10px; cursor:pointer; }
    .ie-mini:hover { background:rgba(0,130,160,.8); }
    .ie-op-baslik { display:flex; justify-content:space-between; align-items:center; color:#5fd9f5; font-weight:600; margin:8px 0 4px; }
    .ie-op { display:grid; grid-template-columns:1fr 50px 18px 20px; gap:4px; align-items:center; margin-bottom:3px; }
    .ie-op .dk { font-size:9px; color:#9fe8f7; } .ie-op input.ad { text-align:left; }
    .ie-sil { background:rgba(120,40,40,.6); border:1px solid rgba(255,100,100,.4); color:#ff9a9a; border-radius:4px; cursor:pointer; }
    .ie-onizle { display:flex; gap:10px; align-items:center; margin:10px 0; padding:8px; background:rgba(0,0,0,.35); border:1px solid rgba(0,200,220,.3); border-radius:6px; }
    .ie-qr { width:80px; height:80px; background:#fff; border-radius:4px; padding:2px; }
    .ie-bb { font-size:14px; font-weight:700; color:#fff; } .ie-bs { font-size:10px; color:#bff; }
    .ie-btn { width:100%; padding:8px; margin-top:4px; border:none; border-radius:5px; font-weight:600; cursor:pointer; color:#fff; background:linear-gradient(90deg,#0a84c0,#0bb39e); }
    .ie-btn.etiket { background:linear-gradient(90deg,#6a3df0,#9b5cff); }
    .ie-btn.kaydet { background:linear-gradient(90deg,#1f9e5a,#34c77b); }
    .ie-kayit-durum { font-size:10px; color:#7dffc0; text-align:center; margin-top:2px; }
    .ie-hata { margin-top:8px; padding:6px; background:rgba(255,50,50,.2); border:1px solid rgba(255,100,100,.4); border-radius:4px; color:#ff9a9a; text-align:center; }
  `]
})
export class FikoIsEmriComponent {
  @Input() anaGosterim: any = null;
  @Input() pencere: any;
  @Input() ayarlar: any = {};
  @Input() modelleme: any = null;

  parcaAd = 'Parça-1'; parcaNo = 'PRT-001'; adet = 1; malzeme = 'Çelik'; olcu = ''; termin = '';
  operasyonlar: IsOperasyon[] = [{ ad: 'Frezeleme', sure: 30 }, { ad: 'Delme', sure: 10 }];
  qrUrl = ''; hata: string | null = null;

  private kalicilik = inject(KalicilikService);
  kayitDurum = '';

  constructor(private cd: ChangeDetectorRef) {
    const yuk = this.kalicilik.yuklenecekAl('is_emri');
    if (yuk) { Object.assign(this, yuk); this.qrYenile(); return; }
    const b = baglamOku();   // önceki analizden gelen parça bağlamı (varsa ön-doldur)
    if (b.guncel) { if (b.malzeme) this.malzeme = b.malzeme; if (b.olcu) this.olcu = b.olcu; }
    this.qrYenile();
  }

  /** İş emrini DB + local'e kaydet. */
  async kaydet(): Promise<void> {
    const veri = { parcaAd: this.parcaAd, parcaNo: this.parcaNo, adet: this.adet, malzeme: this.malzeme, olcu: this.olcu, termin: this.termin, operasyonlar: this.operasyonlar };
    this.kayitDurum = 'Kaydediliyor…'; this.cd.detectChanges();
    const k = await this.kalicilik.kaydet('is_emri', (this.parcaAd || 'İş Emri') + ' · ' + (this.parcaNo || ''), veri);
    this.kayitDurum = k._sync ? '✓ Kaydedildi (DB)' : '✓ Kaydedildi (yerel)'; this.cd.detectChanges();
  }

  toplamSure(): number { return this.operasyonlar.reduce((t, o) => t + (+o.sure || 0), 0); }
  opEkle(): void { this.operasyonlar.push({ ad: 'Operasyon', sure: 0 }); }
  opSil(i: number): void { this.operasyonlar.splice(i, 1); }

  modeldenAl(): void {
    const mesh = this.modelleme?.govdeMesh || this.anaGosterim?.modelleme?.govdeMesh;
    if (!mesh) { this.hata = 'Aktif model yok'; this.cd.detectChanges(); return; }
    const b = new THREE.Box3().setFromObject(mesh); const s = new THREE.Vector3(); b.getSize(s);
    this.olcu = `${s.x.toFixed(0)}×${s.y.toFixed(0)}×${s.z.toFixed(0)}`;
    if (this.modelleme?.aktifMalzemeAd) this.malzeme = this.modelleme.aktifMalzemeAd;
    this.hata = null; this.qrYenile();
  }

  /** QR içeriği: parça künyesi (HMI/okuyucu bunu çözer). */
  private qrIcerik(): string {
    return JSON.stringify({ t: 'fikoyt-is-emri', ad: this.parcaAd, no: this.parcaNo, mlz: this.malzeme, olcu: this.olcu });
  }

  async qrYenile(): Promise<void> {
    try { this.qrUrl = await QRCode.toDataURL(this.qrIcerik(), { margin: 1, width: 160 }); }
    catch { this.qrUrl = ''; }
    this.cd.detectChanges();
  }

  yazdir(): void {
    const w = window.open('', '_blank', 'width=820,height=1000'); if (!w) { this.hata = 'Popup engeli'; this.cd.detectChanges(); return; }
    const tarih = new Date().toLocaleDateString('tr-TR');
    const ops = this.operasyonlar.map((o, i) => `<tr><td>${i + 1}</td><td style="text-align:left">${o.ad}</td><td>${o.sure} dk</td><td></td></tr>`).join('');
    w.document.write(`<!doctype html><html lang="tr"><head><meta charset="utf-8"><title>İş Emri</title>
    <style>body{font-family:Segoe UI,Arial,sans-serif;color:#13283a;padding:28px;max-width:760px;margin:auto}
    h1{color:#0a84c0;margin:0} .head{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid #0a84c0;padding-bottom:12px;margin-bottom:16px}
    .meta{font-size:13px;line-height:1.7} .meta b{color:#0a84c0} img{width:120px;height:120px}
    table{width:100%;border-collapse:collapse;font-size:13px;margin-top:8px} th,td{border:1px solid #cdd9e3;padding:7px;text-align:center} th{background:#0a84c0;color:#fff}
    .imza{margin-top:40px;display:flex;justify-content:space-between;font-size:13px}</style></head><body>
    <div class="head"><div><h1>İŞ EMRİ</h1><div class="meta"><b>Parça:</b> ${this.parcaAd}<br><b>No:</b> ${this.parcaNo}<br><b>Adet:</b> ${this.adet}<br><b>Malzeme:</b> ${this.malzeme}<br><b>Ölçü:</b> ${this.olcu || '—'}<br><b>Termin:</b> ${this.termin || '—'}<br><b>Tarih:</b> ${tarih}</div></div>
    ${this.qrUrl ? `<img src="${this.qrUrl}">` : ''}</div>
    <table><tr><th>#</th><th>Operasyon</th><th>Süre</th><th>İmza/Saat</th></tr>${ops}
    <tr><td colspan="2" style="text-align:right;font-weight:700">Toplam</td><td style="font-weight:700">${this.toplamSure()} dk</td><td></td></tr></table>
    <div class="imza"><div>Hazırlayan: ____________</div><div>Operatör: ____________</div><div>Kontrol: ____________</div></div>
    <script>window.onload=()=>window.print()</script></body></html>`);
    w.document.close();
  }

  etiketYazdir(): void {
    const w = window.open('', '_blank', 'width=420,height=300'); if (!w) { this.hata = 'Popup engeli'; this.cd.detectChanges(); return; }
    w.document.write(`<!doctype html><html lang="tr"><head><meta charset="utf-8"><title>Etiket</title>
    <style>body{font-family:Segoe UI,Arial,sans-serif;margin:0;padding:10px}
    .et{display:flex;gap:12px;align-items:center;border:2px solid #000;border-radius:8px;padding:12px;width:360px}
    img{width:110px;height:110px} .b{font-size:18px;font-weight:800} .s{font-size:13px;color:#333;line-height:1.5}</style></head><body>
    <div class="et">${this.qrUrl ? `<img src="${this.qrUrl}">` : ''}<div><div class="b">${this.parcaAd}</div>
    <div class="s">No: ${this.parcaNo}<br>${this.malzeme} · ${this.olcu || '—'}<br>${this.adet} adet</div></div></div>
    <script>window.onload=()=>window.print()</script></body></html>`);
    w.document.close();
  }
}
